import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import * as yup from 'yup'
import { useTranslation } from 'react-i18next'
import { makeStyles, Grid } from '@material-ui/core'
import Filters from 'components/FilterList/Filters'
import { Form } from 'react-final-form'
import Button from 'components/Button'
import ItemForm, { FieldsForm } from './ItemForm'
import { useSelector, useDispatch } from 'react-redux'
import {
  getEcorregioesTerrestre,
  getIsSavingEcorregioes,
  getIsUpdatingEcorregioes,
} from 'store/modules/ecorregioes/ecorregioes.selectors'
import { validateFormValues } from 'utils/forms'
import { useConfirmationDialog } from 'components/Dialog/ConfirmDialog'
import {
  callCriarEcorregiao,
  callAtualizarEcorregiao,
  callExcluirEcorregiao,
} from 'store/modules/ecorregioes'
import { getTipoUsuarioLogado } from 'store/modules/auth/auth.selectors'
import { TIPO_USUARIO } from 'utils/constantes'

const useStyles = makeStyles(theme => ({
  rowText: {
    display: 'flex',
    alignItems: 'center',
    height: theme.spacing(5),
    fontSize: theme.typography.pxToRem(14),
  },
  search: {
    display: 'flex',
    justifyContent: 'space-between',
    width: props => props.fullWidth && '100%',
  },
  hidden: {
    display: 'none',
  },
  formAdd: {
    transition: '0.20s ease-out',
    maxHeight: 0,
    overflow: 'hidden',
  },
  openFormAdd: {
    marginTop: '-10px',
    paddingTop: 10,
    maxHeight: '1300px',
    transition: '0.25s ease-in',
  },
  normalTop: {
    marginTop: '16px',
  },
  alignRight: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingRight: '0 !important',
  },
  itemAlignRight: {
    maxWidth: '190px',
  },
  listItems: {
    marginTop: '32px',
    border: '1px solid #C9CEDB',
    boxShadow: '0px 8px 32px rgba(15, 52, 68, 0.08)',
    borderRadius: '8px',
    padding: '0px 16px',
  },
  itemTitle: {
    fontFamily: 'Poppins',
    color: '#0F3444',
    fontWeight: '600',
    fontSize: '16px',
  },
  divider: {
    height: 1,
    width: 'calc(100% + 32px)',
    marginLeft: '-16px',
    backgroundColor: theme.palette.grey.main,
  },
  dividerField: {
    borderBottom: `1px solid ${theme.palette.grey.main}`,
    margin: '8px 0',
  },
  itemsForm: {
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    justifyContent: 'space-between',
    rowGap: '20px',
  },
  itemFormField: {
    width: 'calc( 50% - 4px )',
  },
  itemFormFull: {
    width: '100%',
  },
  inputAdornment: {
    marginRight: theme.spacing(2),
  },
}))
const validation = yup.object({
  nome: yup.string().required(),
  hectareAreaOriginal: yup.number().required(),
  percentualAreaDegradada: yup.number().required(),
  link: yup.string().url(),
})
const filterScreen = [
  { value: true, label: 'filtros.nomeAZ', name: 'order' },
  { value: false, label: 'filtros.nomeZA', name: 'order' },
]
const TabTerrestre = forwardRef(({ paisId, controlAdded, setControlAdded }, ref) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const dispatch = useDispatch()
  const isSaving = useSelector(getIsSavingEcorregioes)
  const isUpdating = useSelector(getIsUpdatingEcorregioes)
  const tipoUsuarioLogado = useSelector(getTipoUsuarioLogado)

  const dialog = useConfirmationDialog()
  const listaEcorregioesTerrestre = useSelector(getEcorregioesTerrestre)
  const [data, setData] = useState(listaEcorregioesTerrestre)

  const desabilitarFormulario = Boolean(tipoUsuarioLogado === TIPO_USUARIO.ADMIN.OBSERVADOR)

  const handleUpdate = values => {
    const { id } = values
    const payload = { ...values, paisId }
    delete payload.id
    dispatch(callAtualizarEcorregiao({ id, ecorregiao: payload }))
  }

  const handleSave = (values, form) => {
    const payload = { ...values, paisId, marinho: false, terrestre: true }
    dispatch(callCriarEcorregiao(payload))
    form.reset()
    Object.keys(values).forEach(key => {
      form.resetFieldState(key)
    })
  }

  const handleDelete = (id, ecorregiao) =>
    dialog
      .showConfirmation({
        title: t('dialogs.confirmarExclusao'),
        message: t('dialogs.excluirEcorregiao', {
          ecorregiao,
        }),
      })
      .then(result => {
        if (result) {
          dispatch(callExcluirEcorregiao({ id }))
        }
        return null
      })

  const [openItem, setOpenItem] = useState('')

  const handleExpand = item => {
    setOpenItem(openItem === item ? '' : item)
  }

  useImperativeHandle(ref, () => ({
    handleButtonAdded() {
      setControlAdded(true)
      handleExpand('')
    },
  }))

  const clearFields = (form, values, type) => {
    if (type === 'add') {
      setControlAdded(!controlAdded)
    }
    form.reset()
    Object.keys(values).forEach(key => {
      form.resetFieldState(key)
    })
  }

  useEffect(() => {
    setData(listaEcorregioesTerrestre)
  }, [listaEcorregioesTerrestre])

  return (
    <>
      <div className={clsx(classes.search, controlAdded && classes.hidden)}>
        <Filters
          fullWidth
          items={listaEcorregioesTerrestre}
          setItems={setData}
          origin={{ vertical: 'top', horizontal: 'right' }}
          filterScreen={filterScreen}
        />
      </div>
      <div className={clsx(classes.formAdd, controlAdded && classes.openFormAdd)}>
        <Form
          onSubmit={handleSave}
          initialValues={{
            nome: '',
            hectareAreaOriginal: '',
            hectareAreaRemanescente: null,
            hectareAreaDegradada: null,
            percentualAreaRemanescente: null,
            percentualAreaDegradada: '',
            link: '',
          }}
          validate={validateFormValues(validation)}
          render={({ handleSubmit, form, values, valid }) => (
            <form onSubmit={handleSubmit}>
              <Grid container>
                <FieldsForm classes={classes} disabled={desabilitarFormulario} />
                <Grid item xs={12}>
                  <Grid
                    container
                    direction='row'
                    spacing={2}
                    justifyContent='space-between'
                    className={classes.normalTop}>
                    <Grid item className={classes.deleteButton}>
                      <Button
                        variant='danger'
                        onClick={e => {
                          e.preventDefault()
                          clearFields(form, values, 'add')
                        }}>
                        {t('inputs.cancelar')}
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        type='submit'
                        disabled={!valid || desabilitarFormulario}
                        loading={isSaving}>
                        {t('inputs.salvar')}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </form>
          )}
        />
      </div>
      <div className={classes.listItems}>
        {data.length > 0 &&
          data.map((elem, index) => (
            <ItemForm
              key={elem.id}
              item={elem}
              isOpen={index === openItem}
              onExpand={() => handleExpand(index)}
              onDelete={handleDelete}
              onSubmit={handleUpdate}
              isUpdating={isUpdating}
              disabled={desabilitarFormulario}
              validation={validation}
            />
          ))}
      </div>
    </>
  )
})

TabTerrestre.propTypes = {
  paisId: PropTypes.number,
  controlAdded: PropTypes.bool,
  setControlAdded: PropTypes.func,
}

export default TabTerrestre
